import { useEffect, useState } from "react";
import { Link, NavLink, Navigate, useLocation } from "react-router-dom";
import { api, waktu } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import {
  LayoutGrid, Globe, Package, Users, Wallet, Network, Landmark, FileText,
  BarChart3, ShieldCheck, Settings, Search, Bell, LogOut, ChevronRight, Menu, X,
  GraduationCap, ShoppingBag, XCircle, UserRound,
} from "lucide-react";

export const MODULES = [
  { key: "cms", label: "Website & CMS", to: "/erp/cms", icon: Globe, perm: "cms.read", desc: "Halaman, artikel, dan media" },
  { key: "produk", label: "Produk & Paket", to: "/erp/produk", icon: Package, perm: "product.read", desc: "Paket, jadwal, harga, promo" },
  { key: "booking", label: "Booking & Jamaah", to: "/erp/booking", icon: Users, perm: "booking.read", desc: "Pendaftaran & konfirmasi jamaah", hideForRoles: ["umrah_consultant"] },
  { key: "pembayaran", label: "Pembayaran Manual", to: "/erp/pembayaran", icon: Wallet, perm: "payment.read", desc: "Verifikasi transfer & cicilan", hideForRoles: ["umrah_consultant"] },
  { key: "toko", label: "Toko Online", to: "/erp/toko", icon: ShoppingBag, perm: "booking.read", desc: "Pesanan & pengiriman produk toko" },
  { key: "mitra", label: "Mitra & Komisi", to: "/erp/mitra", icon: Network, perm: "partner.read", desc: "Cabang & konsultan" },
  { key: "portal-mitra", label: "Portal Mitra", to: "/erp/portal-mitra", icon: Network, perm: "partner.portal", desc: "Referral, jamaah, komisi saya" },
  { key: "payout", label: "Pencairan Komisi", to: "/erp/payout", icon: Wallet, perm: "commission.read", desc: "Review & bayar payout", hideForRoles: ["umrah_consultant"] },
  { key: "pembatalan", label: "Refund & Pembatalan", to: "/erp/pembatalan", icon: XCircle, perm: "booking.read", desc: "Credit note & clawback komisi" },
  { key: "keuangan", label: "Keuangan", to: "/erp/keuangan", icon: Landmark, perm: "finance.read", desc: "Jurnal, COA, laporan" },
  { key: "dokumen", label: "Dokumen", to: "/erp/dokumen", icon: FileText, perm: "document.read", desc: "Dokumen jamaah privat", hideForRoles: ["umrah_consultant"] },
  { key: "ecourse", label: "E-Course", to: "/erp/e-course", icon: GraduationCap, perm: "course.write", desc: "Kursus, materi, dan peserta" },
  { key: "laporan", label: "Laporan Operasional", to: "/erp/laporan", icon: BarChart3, perm: "report.read", desc: "Manifest, roomlist, progres dokumen" },
  { key: "pengguna", label: "Pengguna & Hak Akses", to: "/erp/pengguna", icon: ShieldCheck, perm: "user.read", desc: "Role & permission" },
  { key: "pengaturan", label: "Pengaturan", to: "/erp/pengaturan", icon: Settings, perm: "settings.read", desc: "Rekening & sistem" },
];

export function RequireAuth({ children, perm }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex min-h-screen items-center justify-center text-sm text-[#8A867C]">Memuat...</div>;
  if (!user) return <Navigate to="/login" replace />;
  const isSuper = user.roles?.includes("super_admin");
  if (perm && !isSuper && !user.permissions?.includes(perm)) {
    return (
      <div data-testid="access-denied" className="flex min-h-screen flex-col items-center justify-center gap-4 bg-[#FBF9F4] px-6 text-center">
        <ShieldCheck className="text-[#B91C1C]" size={36} />
        <h1 className="font-display text-2xl font-bold text-[#141414]">Akses Ditolak</h1>
        <p className="max-w-md text-sm text-[#4A4844]">Peran Anda tidak memiliki izin untuk membuka modul ini. Hubungi Super Admin jika Anda membutuhkan akses.</p>
        <Link to="/erp" className="btn-gold rounded-xl px-5 py-2.5 text-sm font-bold">Kembali ke Dashboard</Link>
      </div>
    );
  }
  return children;
}

export function ErpLayout({ title, breadcrumb = [], actions, children }) {
  const { user, logout, can } = useAuth();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [showNotif, setShowNotif] = useState(false);
  const hasJamaahPortal = !!user?.roles?.some((role) => ["jamaah", "umrah_consultant"].includes(role));

  useEffect(() => { api.get("/notifications").then((r) => setNotifs(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (q.length < 2) { setResults([]); return; }
    const t = setTimeout(() => { api.get("/admin/search", { params: { q } }).then((r) => setResults(r.data)).catch(() => {}); }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const visible = MODULES.filter((m) => {
    if (!can(m.perm)) return false;
    if (m.hideForRoles?.some((r) => user?.roles?.includes(r))) return false;
    return true;
  });

  return (
    <div className="flex min-h-screen bg-[#F4EFE6]">
      <aside className={`fixed inset-y-0 left-0 z-40 w-64 shrink-0 bg-[#0D0D0D] text-[#D7D0C2] transition-transform lg:static lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="flex items-center justify-between px-5 py-5">
          <Link to="/erp" className="flex items-center gap-2">
            <img src="/logo.png" alt="Royal Saudi Haramain" className="h-7 w-auto" />
            <span className="font-display text-sm font-bold text-white">ERP</span>
          </Link>
          <button className="lg:hidden" onClick={() => setOpen(false)}><X size={18} /></button>
        </div>
        <nav className="mt-4 space-y-1 pb-8" data-testid="erp-sidebar">
          <NavLink to="/erp" end onClick={() => setOpen(false)}
            className={({ isActive }) => `erp-nav-item flex items-center gap-3 px-5 py-2.5 text-sm ${isActive ? "bg-[#1F1F1F] text-[#E5C358]" : "hover:bg-[#1A1A1A]"}`}>
            <LayoutGrid size={16} /> Dashboard
          </NavLink>
          {visible.map((m) => (
            <NavLink key={m.key} to={m.to} onClick={() => setOpen(false)} data-testid={`sidebar-${m.key}`}
              className={({ isActive }) => `erp-nav-item flex items-center gap-3 px-5 py-2.5 text-sm ${isActive ? "bg-[#1F1F1F] text-[#E5C358]" : "hover:bg-[#1A1A1A]"}`}>
              <m.icon size={16} /> <span className="flex-1">{m.label}</span>
              {m.soon && <span className="rounded-full bg-[#8C6D1F]/40 px-2 py-0.5 text-[9px] uppercase">soon</span>}
            </NavLink>
          ))}
        </nav>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex items-center gap-3 border-b border-[#E5DEC9] bg-[#050505] px-4 py-3 text-white lg:px-7">
          <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="erp-menu-toggle"><Menu size={20} /></button>
          <div className="relative flex-1 max-w-md">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8D8677]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Pencarian global..."
              data-testid="erp-global-search"
              className="w-full rounded-xl bg-white/10 py-2 pl-9 pr-3 text-sm text-white placeholder:text-[#8D8677] outline-none focus:bg-white/15" />
            {results.length > 0 && (
              <div data-testid="erp-search-results" className="absolute left-0 right-0 top-11 overflow-hidden rounded-xl border border-[#E5DEC9] bg-white text-[#121212] shadow-xl">
                {results.map((r) => (
                  <Link key={`${r.type}-${r.link}`} to={r.link} onClick={() => { setQ(""); setResults([]); }}
                    className="flex items-center justify-between px-4 py-2.5 text-sm hover:bg-[#F4EFE6]">
                    <span>{r.label}</span><span className="eyebrow">{r.type}</span>
                  </Link>))}
              </div>)}
          </div>
          {hasJamaahPortal && (
            <Link
              to="/portal"
              data-testid="erp-switch-to-jamaah-portal"
              aria-label="Buka Portal Jamaah"
              title="Buka Portal Jamaah"
              className="group flex items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-2.5 py-2 text-xs font-semibold text-[#F6E7A6] transition-colors hover:border-[#D4AF37]/50 hover:bg-[#D4AF37]/15 sm:px-3"
            >
              <UserRound size={18} className="shrink-0" />
              <span className="hidden md:inline">Portal Jamaah</span>
            </Link>
          )}
          <div className="relative">
            <button onClick={() => setShowNotif(!showNotif)} data-testid="erp-notification-btn" className="relative rounded-lg p-2 hover:bg-white/10">
              <Bell size={18} />
              {notifs.some((n) => !n.is_read) && <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-[#D4AF37]" />}
            </button>
            {showNotif && (
              <div data-testid="erp-notification-panel" className="absolute right-0 top-12 w-80 rounded-xl border border-[#E5DEC9] bg-white p-4 text-[#121212] shadow-xl">
                <div className="eyebrow">Notifikasi</div>
                {notifs.length === 0 ? <p className="mt-3 text-sm text-[#8A867C]">Belum ada notifikasi.</p> :
                  notifs.slice(0, 6).map((n) => (
                    <div key={n.id} className="mt-3 border-b border-[#F4EFE6] pb-2 text-sm">
                      <div className="font-semibold">{n.title}</div>
                      <div className="text-xs text-[#8A867C]">{n.body}</div>
                    </div>))}
              </div>)}
          </div>
          <div className="hidden text-right sm:block">
            <div className="text-xs font-semibold">{user?.full_name}</div>
            <div className="text-[10px] text-[#8D8677]">{user?.role_labels?.join(", ")}</div>
          </div>
          <button onClick={logout} data-testid="erp-logout-btn" className="rounded-lg p-2 hover:bg-white/10"><LogOut size={18} /></button>
        </header>

        <div className="px-4 py-6 lg:px-7">
          <nav className="flex flex-wrap items-center gap-2 text-xs text-[#8A867C]" data-testid="erp-breadcrumb">
            <Link to="/erp">Royal ERP</Link>
            {breadcrumb.map((b) => (<span key={b} className="flex items-center gap-2"><ChevronRight size={12} />{b}</span>))}
          </nav>
          <div className="mt-3 flex flex-wrap items-end justify-between gap-3">
            <h1 className="font-display text-2xl font-bold text-[#141414] lg:text-3xl">{title}</h1>
            <div className="flex flex-wrap gap-2">{actions}</div>
          </div>
          <div className="mt-7" key={loc.pathname}>{children}</div>
        </div>
      </div>
      {open && <div className="fixed inset-0 z-30 bg-black/40 lg:hidden" onClick={() => setOpen(false)} />}
    </div>
  );
}

export const Card = ({ children, className = "", ...rest }) => (
  <div className={`rounded-xl border border-[#E5DEC9] bg-white p-5 ${className}`} {...rest}>{children}</div>
);

export const Timeline = ({ items }) => (
  <ol className="space-y-4" data-testid="activity-timeline">
    {items.length === 0 && <li className="text-sm text-[#8A867C]">Belum ada aktivitas.</li>}
    {items.map((a) => (
      <li key={a.id} className="relative border-l border-[#E5DEC9] pl-5">
        <span className="absolute -left-[5px] top-1.5 h-2.5 w-2.5 rounded-full bg-[#C5A028]" />
        <div className="text-sm text-[#121212]">{a.message}</div>
        <div className="text-xs text-[#8A867C]">{a.user_email} · {waktu(a.created_at)}</div>
      </li>))}
  </ol>
);

export const ComingSoon = ({ title, module }) => (
  <ErpLayout title={title} breadcrumb={[title]}>
    <Card className="py-16 text-center" data-testid="coming-soon">
      <div className="eyebrow">Segera Hadir</div>
      <h2 className="mt-3 font-display text-2xl font-semibold text-[#141414]">Modul {module} sedang dipersiapkan</h2>
      <p className="mx-auto mt-3 max-w-xl text-sm text-[#4A4844]">
        Struktur data dan hak akses untuk modul ini sudah tersedia di database. Antarmuka operasional
        akan diaktifkan pada tahap pengembangan berikutnya sehingga tidak ada tombol yang tidak berfungsi.
      </p>
    </Card>
  </ErpLayout>
);

export const Table = ({ columns, rows, testid, empty = "Belum ada data." }) => (
  <div className="overflow-x-auto rounded-xl border border-[#E5DEC9] bg-white">
    <table className="w-full text-sm" data-testid={testid}>
      <thead className="bg-[#F4EFE6] text-left text-xs uppercase tracking-wider text-[#4A4844]">
        <tr>{columns.map((c) => <th key={c} className="whitespace-nowrap p-4">{c}</th>)}</tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr><td colSpan={columns.length} className="p-8 text-center text-[#8A867C]">{empty}</td></tr>
        ) : rows}
      </tbody>
    </table>
  </div>
);
